import { Icon } from "@pepperstone/pepper-ui-kit";
import * as Flags from "country-flag-icons/react/3x2";
import { useEffect, useRef, useState } from "react";

import { FieldError } from "../FieldError";
import { InputV2 } from "../InputV2";
import { Typo } from "../Typo";

export type PhoneInputOption<T = string> = {
  label: string;
  value: T;
};

type PhoneInputProps = {
  id?: string;
  name: string;
  options: PhoneInputOption[];
  value?: string;
  country?: string;
  isMobile?: boolean;
  label?: string;
  subLabel?: string;
  className?: string;
  onSelectFlag: (selected: PhoneInputOption) => void;
  onInput: (value: string) => void;
  onFocus?: () => void;
  error?: string;
  disabled?: boolean;
};

export function PhoneInputV2({
  id,
  name,
  options,
  value,
  className,
  onSelectFlag,
  onInput,
  onFocus,
  country,
  // isMobile,
  label,
  subLabel,
  error,
  disabled,
}: PhoneInputProps) {
  const [selected, setSelected] = useState(
    options.find((e) => e.value === country) || options[0],
  );
  const [open, setOpen] = useState(false);
  const Flag = Flags[selected.value as keyof typeof Flags];
  const showLabel = label || subLabel;
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div id={id} className="flex flex-col gap-2">
      {showLabel && (
        <div className="flex flex-col gap-1">
          {label && (
            <label htmlFor={name}>
              <Typo element="p" type="labelMD">
                {label}
              </Typo>
            </label>
          )}
          {subLabel && (
            <Typo element="p" type="bodyXS" textColor="text-text-secondary">
              {subLabel}
            </Typo>
          )}
        </div>
      )}
      <div className="relative flex items-center gap-3">
        <div className="relative" ref={dropdownRef}>
          <button
            id="country-flag"
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className={`flex items-center gap-1 bg-transparent ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
            disabled={disabled}
          >
            {Flag && (
              <Flag
                title={selected.label}
                className="h-4 w-6 outline outline-1 outline-text-subdued"
              />
            )}
            <div className="pointer-events-none">
              <Icon icon="arrow-down" size="tiny" />
            </div>
          </button>

          {open && (
            <div className="absolute z-10 mt-2 w-max min-w-[200px] rounded-md bg-cta-background-secondary-base shadow-lg">
              <div className="mt-4 max-h-[300px] overflow-y-scroll p-4">
                {options.map((option) => {
                  return (
                    <li
                      key={option.value}
                      onClick={() => {
                        setSelected(option);
                        onSelectFlag(option);
                        setOpen(false);
                      }}
                      className="mb-4 block min-w-[200px] rounded-md p-2 hover:bg-gray-200"
                    >
                      <div className="flex items-center gap-2">
                        <Typo element="p" type="bodySM">
                          {option.label}
                        </Typo>
                      </div>
                    </li>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className={`${className}`}>
          <InputV2
            name={name}
            value={value || ""}
            onChange={(val) => onInput(val.replace(/[^0-9]/g, ""))}
            onFocus={onFocus}
            error={error}
            showError={false}
            disabled={disabled}
          />
        </div>
      </div>
      {error && <FieldError text={error} className="text-text-error" />}
    </div>
  );
}
